import { Box, Divider, Grid2 as Grid, LinearProgress, Link, Paper, Skeleton, Stack, Typography } from '@mui/material'
import { useEffect } from 'react'
import { Link as RouterLink } from 'react-router'
import DateDisplay from '../components/DateDisplay'
import { LabeledValue } from '../components/LabeledValue'
import { NameWithFavicon } from '../components/NameWithFavicon'
import { NotFound } from '../components/NotFound'
import { OrderActions } from '../components/OrderActions'
import OrderNotes from '../components/OrderNotes'
import { OrderTimeline } from '../components/OrderTimeline/OrderTimeline'
import RfqStatusChip from '../components/RfqStatusChip'
import { useOrderContext } from '../contexts/OrderContextProvider'
import { useDocumentTitle } from '../hooks/useDocumentTitle'

export default function OrderPage() {
  const { fetching, rfq } = useOrderContext()
  const setDocumentTitle = useDocumentTitle('')

  useEffect(() => {
    if (rfq) {
      setDocumentTitle(`Order #${rfq.id}`)
    }
  }, [rfq, setDocumentTitle])

  if (fetching && !rfq) {
    return (
      <div>
        <Skeleton variant="text" />
      </div>
    )
  }
  if (!rfq) {
    return <NotFound />
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <LinearProgress
        variant="indeterminate"
        hidden={!fetching}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          visibility: fetching ? 'visible' : 'hidden',
        }}
        color="secondary"
      />
      <Typography variant="h1">Order Details</Typography>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 1, sm: 3 }}
        my={2}
        divider={<Divider sx={{ borderBottomWidth: { xs: 'thin', md: 0 }, borderRightWidth: { xs: 0, md: 'thin' } }} />}
        component={Paper}
        p={2}
      >
        <LabeledValue label="Project">
          <Link component={RouterLink} to={`/projects/${rfq.project.id}`}>
            {rfq.project.name}
          </Link>
        </LabeledValue>
        <LabeledValue label="Client">
          <Link component={RouterLink} to={`/clients/${rfq.client.id}`}>
            <NameWithFavicon name={rfq.client.name} website={rfq.client.website} />
          </Link>
        </LabeledValue>
        <LabeledValue label="Vendor">
          <Link component={RouterLink} to={`/vendors/${rfq.vendor.id}`}>
            <NameWithFavicon name={rfq.vendor.name} website={rfq.vendor.website} />
          </Link>
        </LabeledValue>
        <LabeledValue label="Status">
          <RfqStatusChip status={rfq.status} size="small" sx={{ flexGrow: 0, width: 'auto' }} />
        </LabeledValue>
        <LabeledValue label="PO Number">
          <Typography fontWeight="bold" fontFamily="monospace">
            {rfq.quote?.purchaseOrder?.poNumber || '--'}
          </Typography>
        </LabeledValue>
        <LabeledValue label="Date Created">
          <Typography>
            <DateDisplay absolute date={rfq.dateCreated} />
          </Typography>
        </LabeledValue>

        {rfq.quote?.purchaseOrder?.dateClosed && (
          <LabeledValue label="Date Closed">
            <Typography>
              <DateDisplay absolute date={rfq.quote.purchaseOrder.dateClosed} />
            </Typography>
          </LabeledValue>
        )}
      </Stack>

      <Grid container spacing={{ xs: 2, md: 3 }} maxWidth="xl">
        <Grid display={{ xs: 'block', md: 'none' }} size={12}>
          <Typography variant="h6">Actions</Typography>
          <OrderActions />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <OrderTimeline timelineProps={{ sx: { mt: 0 } }} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Box display={{ xs: 'none', md: 'block' }} mb={2}>
            <Typography variant="h6">Actions</Typography>
            <OrderActions />
          </Box>
          <Typography variant="h6">Order Notes</Typography>
          <OrderNotes />
        </Grid>
      </Grid>
    </Box>
  )
}
